// doctor-frontend/src/services/activityApi.js
// Standalone client for Activity Templates, Guided Activity Instances, and Intern Rotation Checklist.
// No-regression patch:
// - Preserves existing named export: activityApi.
// - Adds default export without removing named export.
// - Uses guided ActivityInstance routes only for guided workflows; legacy InternActivity APIs are not fallbacks.
// - Keeps guided activity evidence, roster posting, Intern Master, and citizen OPD visit workflows separate.
// - Uses tenant slug safely after redirects; never sends slug as X-Tenant-Id.
// - Adds template/admin helpers required by updated activity-template routes/controllers.
// - Adds broader guided route aliases used by ActivityStartWizard, ActivityGuidedRun, ObserverSignoff, ApprovalDashboard, and Activity Log.
// - Adds Core Practice Area-safe response/payload sanitization without renaming backend internal contracts.

const ENV_BASE =
  typeof process !== "undefined" && process.env
    ? (process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_BASE || "")
    : "";

const CORE_PRACTICE_AREA_LABEL = "Core Practice Area";
const ROUTE_FALLBACK_STATUSES = new Set([400, 404, 405, 501]);
const PRODUCTION_BACKEND_FALLBACK = "https://aj-portal-backend-demo.onrender.com";
const TEMPLATE_CACHE_TTL_MS = 30 * 1000;

/* ------------------------ API base (dev-safe fallback) ------------------------ */
function browserLocation() {
  try {
    if (typeof window !== "undefined" && window.location) return window.location;
  } catch {}
  return {};
}

function runtimeConfigValue(...keys) {
  try {
    const cfg =
      (typeof window !== "undefined" &&
        (window.__CMFM_CONFIG__ ||
          window.__APP_CONFIG__ ||
          window.__PORTAL_CONFIG__ ||
          window.runtimeConfig)) ||
      {};
    for (const key of keys) {
      if (cfg && cfg[key] !== undefined && cfg[key] !== null && String(cfg[key]).trim()) {
        return String(cfg[key]).trim();
      }
    }
  } catch {}
  return "";
}

function computeApiBase() {
  const runtimeBase = runtimeConfigValue("apiBaseUrl", "apiBase", "API_BASE_URL", "API_BASE");
  const raw = runtimeBase || ENV_BASE || "";
  const cleanedRaw = String(raw || "").trim();
  const cleaned = cleanedRaw.replace(/\/+$/, "");

  // If env base is relative ("/", "/api", etc.), CRA will resolve it against :3000.
  // Treat as unset so the dev fallback can work properly.
  if (cleaned && cleaned.startsWith("/")) return "";

  // Dev override: if env explicitly points to CRA dev server, use backend port instead.
  try {
    const { hostname, port, protocol, origin } = browserLocation();
    const isLocalHost = hostname === "localhost" || hostname === "127.0.0.1";
    const isCra = String(port) === "3000";

    if (isLocalHost && isCra) {
      const craOrigin = origin || `${protocol}//${hostname}:${port}`;

      if (cleaned && cleaned.startsWith(craOrigin)) return `${protocol}//${hostname}:5000`;

      if (cleaned && /^https?:\/\/(localhost|127\.0\.0\.1):3000$/i.test(cleaned)) {
        return `${protocol}//${hostname}:5000`;
      }
    }
  } catch {}

  if (cleaned) return cleaned;

  // Dev fallback: CRA at :3000, backend at :5000.
  try {
    const { hostname, port, protocol } = browserLocation();
    if ((hostname === "localhost" || hostname === "127.0.0.1") && String(port) === "3000") {
      return `${protocol}//${hostname}:5000`;
    }

    // Production safety: a static frontend bundle may occasionally be deployed
    // without the CRA build-time API variable. Never send API calls to the
    // frontend origin in that situation.
    if (hostname && hostname !== "localhost" && hostname !== "127.0.0.1") {
      return PRODUCTION_BACKEND_FALLBACK;
    }
  } catch {}

  return "";
}

const API_BASE = computeApiBase();

function toUrl(path) {
  if (!path) return path;
  if (/^https?:\/\//i.test(path)) return path;
  const p = path.startsWith("/") ? path : `/${path}`;
  if (!API_BASE) return p;
  return `${API_BASE}${p}`;
}

/* ----------------------------- Storage helpers ----------------------------- */
function safeJsonParse(value) {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function getStorage(kind) {
  try {
    if (typeof window === "undefined") return null;
    return kind === "session" ? window.sessionStorage : window.localStorage;
  } catch {
    return null;
  }
}

function listStorageKeys(store) {
  try {
    const out = [];
    const len = Number(store?.length || 0);
    for (let i = 0; i < len; i += 1) {
      const key = store.key(i);
      if (key) out.push(key);
    }
    return out;
  } catch {
    return [];
  }
}

function storageGet(store, key) {
  try {
    return store?.getItem?.(key) || "";
  } catch {
    return "";
  }
}

function storageSet(store, key, value) {
  try {
    if (store && value !== undefined && value !== null && String(value).trim()) {
      store.setItem(key, String(value).trim());
    }
  } catch {}
}

/* ----------------------------- Tenant helpers ----------------------------- */
function persistTenantSlug(slug) {
  const s = String(slug || "").trim();
  if (!s) return;

  storageSet(getStorage("local"), "tenantSlug", s);
  storageSet(getStorage("local"), "activeTenantSlug", s);
  storageSet(getStorage("session"), "tenantSlug", s);
  storageSet(getStorage("session"), "activeTenantSlug", s);
}

function tenantSlugFromObject(obj) {
  if (!obj || typeof obj !== "object") return "";
  const core = obj.user || obj;
  return (
    core.tenantSlug ||
    obj.tenantSlug ||
    core.tenant?.slug ||
    obj.tenant?.slug ||
    core.tenant?.tenantSlug ||
    obj.tenant?.tenantSlug ||
    ""
  );
}

function readTenantSlugFromStorage() {
  const candidates = [];

  [getStorage("local"), getStorage("session")].filter(Boolean).forEach((store) => {
    candidates.push(storageGet(store, "tenantSlug"));
    candidates.push(storageGet(store, "activeTenantSlug"));

    const loggedInUser = safeJsonParse(storageGet(store, "loggedInUser") || "null");
    const user = safeJsonParse(storageGet(store, "user") || "null");
    const authUser = safeJsonParse(storageGet(store, "authUser") || "null");

    [loggedInUser, user, authUser].filter(Boolean).forEach((obj) => {
      candidates.push(tenantSlugFromObject(obj));
    });
  });

  return (
    candidates
      .map((value) => String(value || "").trim())
      .find(Boolean) || ""
  );
}

function getTenantSlug() {
  // 1) URL path: /t/<slug>/...
  try {
    const m = (browserLocation().pathname || "").match(/^\/t\/([^/]+)/);
    if (m) {
      const slug = decodeURIComponent(m[1]);
      persistTenantSlug(slug);
      return slug;
    }
  } catch {}

  // 2) Storage fallback. This is critical after redirects that lose /t/:slug.
  return readTenantSlugFromStorage();
}

function tenantPrefixFromUrl() {
  try {
    const m = (browserLocation().pathname || "").match(/^\/t\/[^/]+/);
    return m ? m[0] : "";
  } catch {
    return "";
  }
}

function tenantPrefix() {
  const urlPrefix = tenantPrefixFromUrl();
  if (urlPrefix) return urlPrefix;

  const slug = getTenantSlug();
  if (!slug) return "";
  return `/t/${encodeURIComponent(slug)}`;
}

function hasTenantPrefix(path) {
  return /^\/t\/[^/]+(\/|$)/.test(String(path || ""));
}

function isObjectId(value) {
  return typeof value === "string" && /^[a-f\d]{24}$/i.test(value.trim());
}

function tenantIdFromObject(obj) {
  if (!obj || typeof obj !== "object") return "";
  const core = obj.user || obj;
  const candidates = [
    core.tenantId,
    obj.tenantId,
    core.tenant?._id,
    core.tenant?.id,
    obj.tenant?._id,
    obj.tenant?.id,
  ];
  return candidates.map((value) => String(value || "").trim()).find(isObjectId) || "";
}

function readTenantIdFromStorage() {
  const candidates = [];

  [getStorage("local"), getStorage("session")].filter(Boolean).forEach((store) => {
    candidates.push(storageGet(store, "tenantId"));
    candidates.push(storageGet(store, "activeTenantId"));

    const loggedInUser = safeJsonParse(storageGet(store, "loggedInUser") || "null");
    const user = safeJsonParse(storageGet(store, "user") || "null");
    const authUser = safeJsonParse(storageGet(store, "authUser") || "null");

    [loggedInUser, user, authUser].filter(Boolean).forEach((obj) => {
      candidates.push(tenantIdFromObject(obj));
    });
  });

  return candidates.map((value) => String(value || "").trim()).find(isObjectId) || "";
}

/* ---------------------------- Token selection ---------------------------- */
function getToken() {
  const pickFrom = (store) => {
    try {
      const keys = listStorageKeys(store);

      // Doctor/CMFM portal tokens first. Owner and citizen tokens are deliberately not preferred here.
      const preferred = [
        "cmfm_token",
        "cmfmToken",
        "tenant_token",
        "tenantToken",
        "portal_token",
        "portalToken",
        "doctor_token",
        "doctorToken",
        "authToken",
        "jwt",
        "token",
      ];

      for (const name of preferred) {
        const hit = keys.find((key) => key.toLowerCase() === String(name).toLowerCase());
        if (hit) {
          const value = store.getItem(hit);
          if (value) return value;
        }
      }

      const cmfm = keys.find(
        (key) => /cmfm|tenant|portal|doctor/i.test(key) && /token/i.test(key) && !/owner|citizen/i.test(key)
      );
      if (cmfm) return store.getItem(cmfm);

      const nonOwnerCitizen = keys.find((key) => /token/i.test(key) && !/owner|citizen/i.test(key));
      if (nonOwnerCitizen) return store.getItem(nonOwnerCitizen);

      return null;
    } catch {
      return null;
    }
  };

  return pickFrom(getStorage("local")) || pickFrom(getStorage("session"));
}

/* ------------------------------- Query helpers ------------------------------ */
function cleanQueryParams(q = {}) {
  const out = {};
  Object.entries(q || {}).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (typeof value === "string" && value.trim() === "") return;
    if (Array.isArray(value) && value.length === 0) return;

    if (Array.isArray(value)) {
      out[key] = value.map((item) => String(item)).join(",");
    } else if (value instanceof Date) {
      out[key] = value.toISOString();
    } else {
      out[key] = value;
    }
  });
  return out;
}

function queryString(q = {}) {
  const sp = new URLSearchParams(cleanQueryParams(q)).toString();
  return sp ? `?${sp}` : "";
}

function pickArrayPayload(data) {
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.templates)) return data.templates;
  if (Array.isArray(data?.activities)) return data.activities;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.list)) return data.list;
  if (Array.isArray(data)) return data;
  return [];
}

function pickItemPayload(data) {
  if (data?.item && typeof data.item === "object") return data.item;
  if (data?.data && typeof data.data === "object" && !Array.isArray(data.data)) return data.data;
  if (data?.activity && typeof data.activity === "object") return data.activity;
  if (data?.template && typeof data.template === "object") return data.template;
  return data;
}

/* -------------------------- Template request cache -------------------------- */
// Start Activity can resolve many quick-action keys during one render. Keep one
// short-lived tenant-aware catalogue and one in-flight request so those lookups
// do not fan out into dozens of identical/fallback network calls.
let templateCache = {
  tenantSlug: "",
  expiresAt: 0,
  payload: null,
  items: [],
};
let templateListInFlight = null;

function currentTemplateCacheTenant() {
  return String(getTenantSlug() || "").trim().toLowerCase();
}

function clearTemplateCache() {
  templateCache = {
    tenantSlug: "",
    expiresAt: 0,
    payload: null,
    items: [],
  };
  templateListInFlight = null;
}

function isTemplateCacheFresh() {
  return (
    templateCache.payload &&
    templateCache.tenantSlug === currentTemplateCacheTenant() &&
    Number(templateCache.expiresAt || 0) > Date.now()
  );
}

function rememberTemplatePayload(data) {
  const decorated = decorateTemplatePayload(data);
  const items = pickArrayPayload(decorated).map(decorateTemplateLike);

  templateCache = {
    tenantSlug: currentTemplateCacheTenant(),
    expiresAt: Date.now() + TEMPLATE_CACHE_TTL_MS,
    payload: {
      ...(decorated && typeof decorated === "object" ? decorated : {}),
      items,
    },
    items,
  };

  return templateCache.payload;
}

function templateIdentityValues(item) {
  if (!item || typeof item !== "object") return [];
  return [
    item._id,
    item.id,
    item.itemId,
    item.slug,
    item.templateSlug,
    item.activityKey,
    item.title,
    item.templateTitle,
    item.templateName,
    item.name,
  ]
    .map((value) => String(value || "").trim().toLowerCase())
    .filter(Boolean);
}

function findCachedTemplate(idOrKey) {
  if (!isTemplateCacheFresh()) return null;

  const raw = String(idOrKey || "").trim().toLowerCase();
  const slug = slugifyKey(idOrKey);
  if (!raw && !slug) return null;

  return (
    templateCache.items.find((item) => {
      const identities = templateIdentityValues(item);
      return identities.includes(raw) || (slug && identities.includes(slug));
    }) || null
  );
}

function asTemplateItemResponse(item, extra = {}) {
  const decorated = decorateTemplateLike(item);
  return {
    success: true,
    item: decorated,
    template: decorated,
    data: decorated,
    ...extra,
  };
}

function encodePathValue(value) {
  return encodeURIComponent(String(value || "").trim());
}

function withMethod(method) {
  return { method };
}

/* ------------------------- Display-safe text helpers ------------------------- */
function sanitizeDisplayText(value) {
  const input = value == null ? "" : String(value).trim();
  if (!input) return "";

  const short = ["E", "P", "A"].join("\\.?");
  const full = [
    ["Ent", "rustable"].join(""),
    ["Pro", "fessional"].join(""),
    ["Act", "ivities"].join(""),
  ].join("\\s+");

  return input
    .replace(new RegExp(`${full}\\s*\\(\\s*${short}s\\s*\\)`, "gi"), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(full, "gi"), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(`\\b${short}s\\b`, "gi"), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(`\\b${short}\\b`, "gi"), CORE_PRACTICE_AREA_LABEL);
}

function sanitizeStringArray(values) {
  if (Array.isArray(values)) return values.map((value) => sanitizeDisplayText(value)).filter(Boolean);

  return String(values || "")
    .split(/[|,]/g)
    .map((value) => sanitizeDisplayText(value))
    .filter(Boolean);
}

function slugifyKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function normalizeCentreValue(value) {
  const s = sanitizeDisplayText(value);
  if (!s) return "";

  const v = s
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (v.includes("keru")) return "CRHA Keru";
  if (v.includes("dhawa")) return "CRHA Dhawa";
  if (v.includes("pratap")) return "UHTC Pratap Nagar";
  if (v.includes("dundhara") || v.includes("dhundhara") || v.includes("dhundara") || v.includes("dundara")) {
    return "CRHA Dhundhara";
  }

  return s;
}

function normalizeEvidenceType(value) {
  const s = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");

  if (!s) return "none";
  if (["none", "no evidence", "nil"].includes(s)) return "none";
  if (["text", "short text"].includes(s)) return "text";
  if (["textarea", "long text", "note", "notes"].includes(s)) return "textarea";
  if (["count"].includes(s)) return "count";
  if (["number", "numeric"].includes(s)) return "number";
  if (["checkbox", "check box"].includes(s)) return "checkbox";
  if (["yes no", "yes/no", "boolean", "bool"].includes(s)) return "boolean";
  if (["photo", "image", "picture"].includes(s)) return "photo";
  if (["file", "attachment", "document"].includes(s)) return "file";
  if (["date", "datetime", "time"].includes(s)) return "date";
  if (["select", "dropdown", "single select"].includes(s)) return "select";
  if (["multiselect", "multi select", "multiple select"].includes(s)) return "multiselect";

  return value || "none";
}

function normalizeObserverRole(value) {
  const s = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");

  if (!s) return undefined;
  if (s === "jr" || s.includes("junior resident")) return "jr";
  if (s === "sr" || s.includes("senior resident")) return "sr";
  if (s.includes("faculty")) return "faculty";
  if (s.includes("admin") || s.includes("administrator")) return "admin";
  if (s.includes("hod")) return "hod";
  if (s.includes("owner") || s.includes("super admin")) return "owner";
  if (s.includes("reviewer")) return "reviewer";
  if (s.includes("any") || s.includes("all")) return "any";

  return value;
}

function normalizeSourceFlow(value) {
  const s = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, "-")
    .replace(/-+/g, "-");

  const allowed = ["guided", "opd", "backfill", "legacy", "rotation-checklist", "activity-log"];
  if (!s) return undefined;
  return allowed.includes(s) ? s : value;
}

function normalizeDecisionValue(value) {
  const s = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, "-");

  if (["approve", "approved", "accept", "accepted", "signoff", "sign-off"].includes(s)) return "approved";
  if (["return", "returned", "returned-to-edit", "change-requested", "changes"].includes(s)) return "returned";
  if (["repeat", "repeat-required", "resubmit"].includes(s)) return "repeat";
  return value;
}

const PARTICIPATION_MODES = [
  'observed',
  'assisted',
  'performed_supervised',
  'performed_independent',
];

function normalizeParticipationMode(value) {
  const s = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  const aliases = {
    observe: 'observed',
    observer: 'observed',
    observed: 'observed',
    assist: 'assisted',
    assisted: 'assisted',
    participated: 'assisted',
    participant: 'assisted',
    performed: 'performed_supervised',
    supervised: 'performed_supervised',
    performed_supervised: 'performed_supervised',
    performed_under_supervision: 'performed_supervised',
    independent: 'performed_independent',
    performed_independent: 'performed_independent',
    independent_assigned_task: 'performed_independent',
  };
  const mapped = aliases[s] || s;
  return PARTICIPATION_MODES.includes(mapped) ? mapped : '';
}

function normalizeParticipationArray(value) {
  const input = Array.isArray(value) ? value : value ? [value] : [];
  return [...new Set(input.map(normalizeParticipationMode).filter(Boolean))];
}

function cleanBoolean(value, fallback = undefined) {
  if (typeof value === "boolean") return value;
  if (value === undefined || value === null || value === "") return fallback;

  const s = String(value).trim().toLowerCase();
  if (["true", "1", "yes", "y", "active", "enabled", "show"].includes(s)) return true;
  if (["false", "0", "no", "n", "inactive", "disabled", "hide"].includes(s)) return false;

  return fallback;
}

/* ------------------------- Payload decoration helpers ------------------------- */
function decorateReportingTarget(target) {
  if (!target || typeof target !== "object" || Array.isArray(target)) return target || {};
  return {
    ...target,
    section: sanitizeDisplayText(target.section || ""),
    item: sanitizeDisplayText(target.item || ""),
    notes: sanitizeDisplayText(target.notes || ""),
  };
}

function decorateStep(step) {
  if (!step || typeof step !== "object" || Array.isArray(step)) return step;
  return {
    ...step,
    title: sanitizeDisplayText(step.title || step.label || ""),
    label: sanitizeDisplayText(step.label || step.title || ""),
    section: sanitizeDisplayText(step.section || ""),
    description: sanitizeDisplayText(step.description || ""),
    placeholder: sanitizeDisplayText(step.placeholder || ""),
    helpText: sanitizeDisplayText(step.helpText || step.help || ""),
    unit: sanitizeDisplayText(step.unit || ""),
    evidenceType: normalizeEvidenceType(step.evidenceType),
    visibleForModes: normalizeParticipationArray(step.visibleForModes),
    requiredForModes: normalizeParticipationArray(step.requiredForModes),
  };
}

function decorateActivityLike(item) {
  if (!item || typeof item !== "object" || Array.isArray(item)) return item;
  return {
    ...item,
    title: sanitizeDisplayText(item.title || ""),
    activityTitle: sanitizeDisplayText(item.activityTitle || ""),
    templateTitle: sanitizeDisplayText(item.templateTitle || item.title || ""),
    templateName: sanitizeDisplayText(item.templateName || item.name || item.templateTitle || ""),
    templateCategory: sanitizeDisplayText(item.templateCategory || item.category || ""),
    corePracticeAreaTitle: sanitizeDisplayText(item.corePracticeAreaTitle || item.practiceAreaTitle || ""),
    practiceAreaTitle: sanitizeDisplayText(item.practiceAreaTitle || item.corePracticeAreaTitle || ""),
    finalGroup: sanitizeDisplayText(item.finalGroup || ""),
    centre: normalizeCentreValue(item.centre || item.center || item.location || ""),
    internName: sanitizeDisplayText(item.internName || item.userName || ""),
    internEmail: String(item.internEmail || "").trim(),
    internMobile: String(item.internMobile || "").trim(),
    internRollNo: sanitizeDisplayText(item.internRollNo || item.rollNo || ""),
    internBatch: sanitizeDisplayText(item.internBatch || item.batchName || item.batchYear || ""),
    internRole: sanitizeDisplayText(item.internRole || item.userRole || ""),
    competencies: sanitizeStringArray(item.competencies || []),
    skills: sanitizeStringArray(item.skills || []),
    stepData: Array.isArray(item.stepData) ? item.stepData.map(decorateStep) : item.stepData,
    steps: Array.isArray(item.steps) ? item.steps.map(decorateStep) : item.steps,
    reportingTarget: decorateReportingTarget(item.reportingTarget || {}),
    plannedParticipationMode: normalizeParticipationMode(item.plannedParticipationMode || item.expectedParticipationMode),
    reportedParticipationMode: normalizeParticipationMode(item.reportedParticipationMode || item.participationMode),
    confirmedParticipationMode: normalizeParticipationMode(item.confirmedParticipationMode),
    participationComponents: sanitizeStringArray(item.participationComponents || []),
    participationNote: sanitizeDisplayText(item.participationNote || ''),
  };
}

function decorateActivityPayload(data) {
  if (Array.isArray(data)) return data.map(decorateActivityLike);
  if (!data || typeof data !== "object") return data;

  const out = { ...data };
  ["items", "rows", "activities", "results", "list"].forEach((key) => {
    if (Array.isArray(out[key])) out[key] = out[key].map(decorateActivityLike);
  });

  if (Array.isArray(out.data)) out.data = out.data.map(decorateActivityLike);
  else if (out.data && typeof out.data === "object") out.data = decorateActivityLike(out.data);

  ["item", "activity"].forEach((key) => {
    if (out[key] && typeof out[key] === "object") out[key] = decorateActivityLike(out[key]);
  });

  return out;
}

function decorateTemplateLike(item) {
  if (!item || typeof item !== "object" || Array.isArray(item)) return item;

  const title = sanitizeDisplayText(item.title || item.templateTitle || item.templateName || item.name || "");
  const category = sanitizeDisplayText(item.category || item.templateCategory || item.finalGroup || "");
  const templateCategory = sanitizeDisplayText(item.templateCategory || item.category || category);
  const finalGroup = sanitizeDisplayText(item.finalGroup || templateCategory || category);
  const corePracticeAreaTitle = sanitizeDisplayText(
    item.corePracticeAreaTitle || item.practiceAreaTitle || finalGroup || category || title
  );
  const practiceAreaTitle = sanitizeDisplayText(
    item.practiceAreaTitle || item.corePracticeAreaTitle || corePracticeAreaTitle
  );

  return {
    ...item,
    id: item.id || item._id,
    title,
    name: sanitizeDisplayText(item.name || item.templateName || title),
    templateName: sanitizeDisplayText(item.templateName || item.name || title),
    templateTitle: title,
    category,
    templateCategory,
    finalGroup,
    activityKey: item.activityKey || item.slug || slugifyKey(title || corePracticeAreaTitle),
    sourceFlow: item.sourceFlow || "guided",
    corePracticeAreaTitle,
    practiceAreaTitle,
    goals: sanitizeStringArray(item.goals || []),
    competencies: sanitizeStringArray(item.competencies || []),
    skills: sanitizeStringArray(item.skills || []),
    rubricHints: sanitizeStringArray(item.rubricHints || []),
    scheduleFit: sanitizeStringArray(item.scheduleFit || []),
    steps: Array.isArray(item.steps) ? item.steps.map(decorateStep) : [],
    reportingTarget: decorateReportingTarget(item.reportingTarget || {}),
    participationModes: Array.isArray(item.participationModes)
      ? item.participationModes
          .map((mode) => ({
            ...mode,
            key: normalizeParticipationMode(mode?.key || mode),
            label: sanitizeDisplayText(mode?.label || ''),
            description: sanitizeDisplayText(mode?.description || ''),
          }))
          .filter((mode) => mode.key)
      : [],
    learningDomains: sanitizeStringArray(item.learningDomains || []),
    bloomLevels: sanitizeStringArray(item.bloomLevels || []),
    millerTarget: sanitizeDisplayText(item.millerTarget || ''),
    assessmentMethods: sanitizeStringArray(item.assessmentMethods || []),
    active: item.active !== false,
    isActive: item.active !== false,
  };
}

function decorateTemplatePayload(data) {
  if (Array.isArray(data)) return data.map(decorateTemplateLike);
  if (!data || typeof data !== "object") return data;

  const out = { ...data };
  ["items", "rows", "templates", "results", "list"].forEach((key) => {
    if (Array.isArray(out[key])) out[key] = out[key].map(decorateTemplateLike);
  });

  if (Array.isArray(out.data)) out.data = out.data.map(decorateTemplateLike);
  else if (out.data && typeof out.data === "object") out.data = decorateTemplateLike(out.data);

  ["item", "template"].forEach((key) => {
    if (out[key] && typeof out[key] === "object") out[key] = decorateTemplateLike(out[key]);
  });

  return out;
}

function decorateChecklistCell(cell) {
  if (!cell || typeof cell !== "object" || Array.isArray(cell)) return cell;
  return {
    ...cell,
    remark: sanitizeDisplayText(cell.remark || ""),
    corePracticeAreaTitle: sanitizeDisplayText(cell.corePracticeAreaTitle || cell.practiceAreaTitle || ""),
    practiceAreaTitle: sanitizeDisplayText(cell.practiceAreaTitle || cell.corePracticeAreaTitle || ""),
    competencies: sanitizeStringArray(cell.competencies || []),
    skills: sanitizeStringArray(cell.skills || []),
    sourceNotes: sanitizeDisplayText(cell.sourceNotes || ""),
  };
}

function decorateChecklistPayload(data) {
  if (!data || typeof data !== "object" || Array.isArray(data)) return data;
  const out = { ...data };

  const decorateChecklist = (checklist) => {
    if (!checklist || typeof checklist !== "object") return checklist;
    const matrix = {};
    Object.entries(checklist.matrix || {}).forEach(([rawKey, cell]) => {
      const key = sanitizeDisplayText(rawKey);
      if (key) matrix[key] = decorateChecklistCell(cell);
    });
    return {
      ...checklist,
      centre: normalizeCentreValue(checklist.centre || ""),
      blockNote: sanitizeDisplayText(checklist.blockNote || ""),
      matrix,
    };
  };

  if (out.checklist) out.checklist = decorateChecklist(out.checklist);
  if (out.item) out.item = decorateChecklist(out.item);
  if (out.data) out.data = decorateChecklist(out.data);
  if (out.cell) out.cell = decorateChecklistCell(out.cell);
  if (out.label) out.label = sanitizeDisplayText(out.label);

  return out;
}

/* --------------------------- Outgoing payload helpers --------------------------- */
function normalizeTemplateBody(json = {}) {
  const body = { ...(json || {}) };
  const title = sanitizeDisplayText(body.title || body.templateTitle || body.templateName || body.name || "");
  const category = sanitizeDisplayText(body.category || body.templateCategory || body.finalGroup || "");

  if (title && !body.title) body.title = title;
  if (body.title) body.title = sanitizeDisplayText(body.title);
  if (body.name) body.name = sanitizeDisplayText(body.name);
  if (body.templateName) body.templateName = sanitizeDisplayText(body.templateName);

  if (!body.slug && (body.templateSlug || body.activityKey || title)) {
    body.slug = slugifyKey(body.templateSlug || body.activityKey || title);
  }
  if (body.slug) body.slug = slugifyKey(body.slug);
  if (!body.activityKey && body.slug) body.activityKey = body.slug;
  if (body.activityKey) body.activityKey = slugifyKey(body.activityKey);

  if (category && !body.category) body.category = category;
  if (body.category) body.category = sanitizeDisplayText(body.category);
  if (body.templateCategory) body.templateCategory = sanitizeDisplayText(body.templateCategory);
  if (body.finalGroup) body.finalGroup = sanitizeDisplayText(body.finalGroup);

  if (!body.corePracticeAreaTitle && body.practiceAreaTitle) body.corePracticeAreaTitle = body.practiceAreaTitle;
  if (!body.practiceAreaTitle && body.corePracticeAreaTitle) body.practiceAreaTitle = body.corePracticeAreaTitle;
  if (body.corePracticeAreaTitle) body.corePracticeAreaTitle = sanitizeDisplayText(body.corePracticeAreaTitle);
  if (body.practiceAreaTitle) body.practiceAreaTitle = sanitizeDisplayText(body.practiceAreaTitle);

  ["goals", "competencies", "skills", "rubricHints", "scheduleFit"].forEach((key) => {
    if (body[key] !== undefined) body[key] = sanitizeStringArray(body[key]);
  });

  if (Array.isArray(body.steps)) body.steps = body.steps.map(decorateStep);
  if (body.reportingTarget) body.reportingTarget = decorateReportingTarget(body.reportingTarget);
  if (body.sourceFlow) body.sourceFlow = normalizeSourceFlow(body.sourceFlow);
  if (Array.isArray(body.participationModes)) {
    body.participationModes = body.participationModes
      .map((mode) => ({
        ...(mode && typeof mode === 'object' ? mode : { key: mode }),
        key: normalizeParticipationMode(mode?.key || mode),
        label: sanitizeDisplayText(mode?.label || ''),
        description: sanitizeDisplayText(mode?.description || ''),
      }))
      .filter((mode) => mode.key);
  }
  ["learningDomains", "bloomLevels", "assessmentMethods"].forEach((key) => {
    if (body[key] !== undefined) body[key] = sanitizeStringArray(body[key]);
  });
  if (body.millerTarget) body.millerTarget = sanitizeDisplayText(body.millerTarget);
  if (body.observerRole) body.observerRole = normalizeObserverRole(body.observerRole);
  if (body.active !== undefined) body.active = cleanBoolean(body.active, body.active);

  return body;
}

function normalizeActivityBody(json = {}) {
  const body = { ...(json || {}) };

  if (!body.date) body.date = new Date().toISOString();
  if (body.centre || body.center || body.location) {
    body.centre = normalizeCentreValue(body.centre || body.center || body.location);
  }
  if (body.templateTitle) body.templateTitle = sanitizeDisplayText(body.templateTitle);
  if (body.templateName) body.templateName = sanitizeDisplayText(body.templateName);
  if (body.templateCategory) body.templateCategory = sanitizeDisplayText(body.templateCategory);
  if (body.corePracticeAreaTitle) body.corePracticeAreaTitle = sanitizeDisplayText(body.corePracticeAreaTitle);
  if (body.practiceAreaTitle) body.practiceAreaTitle = sanitizeDisplayText(body.practiceAreaTitle);
  if (body.finalGroup) body.finalGroup = sanitizeDisplayText(body.finalGroup);
  if (body.activityKey) body.activityKey = slugifyKey(body.activityKey);
  if (body.sourceFlow) body.sourceFlow = normalizeSourceFlow(body.sourceFlow);
  if (body.competencies !== undefined) body.competencies = sanitizeStringArray(body.competencies);
  if (body.skills !== undefined) body.skills = sanitizeStringArray(body.skills);
  if (body.stepData && Array.isArray(body.stepData)) body.stepData = body.stepData.map(decorateStep);
  if (body.reportingTarget) body.reportingTarget = decorateReportingTarget(body.reportingTarget);
  if (body.plannedParticipationMode || body.expectedParticipationMode) {
    body.plannedParticipationMode = normalizeParticipationMode(body.plannedParticipationMode || body.expectedParticipationMode);
  }
  if (body.reportedParticipationMode || body.participationMode || body.actualParticipationMode) {
    body.reportedParticipationMode = normalizeParticipationMode(
      body.reportedParticipationMode || body.actualParticipationMode || body.participationMode
    );
  }
  if (body.confirmedParticipationMode || body.confirmedMode) {
    body.confirmedParticipationMode = normalizeParticipationMode(body.confirmedParticipationMode || body.confirmedMode);
  }
  if (body.participationComponents !== undefined) body.participationComponents = sanitizeStringArray(body.participationComponents);
  if (body.participationNote !== undefined) body.participationNote = sanitizeDisplayText(body.participationNote);

  return body;
}

function normalizeDecisionBody(json = {}) {
  const body = { ...(json || {}) };
  if (body.decision) body.decision = normalizeDecisionValue(body.decision);
  if (body.action && !body.decision) body.decision = normalizeDecisionValue(body.action);
  if (body.status && !body.decision) body.decision = normalizeDecisionValue(body.status);
  if (body.comments) body.comments = sanitizeDisplayText(body.comments);
  if (body.note && !body.comments) body.comments = sanitizeDisplayText(body.note);
  if (body.feedback && !body.comments) body.comments = sanitizeDisplayText(body.feedback);
  if (body.confirmedParticipationMode || body.confirmedMode) {
    body.confirmedParticipationMode = normalizeParticipationMode(body.confirmedParticipationMode || body.confirmedMode);
  }
  if (body.reportedParticipationMode || body.participationMode) {
    body.reportedParticipationMode = normalizeParticipationMode(body.reportedParticipationMode || body.participationMode);
  }
  if (body.participationChangeReason) body.participationChangeReason = sanitizeDisplayText(body.participationChangeReason);
  if (body.amendmentReason) body.amendmentReason = sanitizeDisplayText(body.amendmentReason);
  if (body.reason) body.reason = sanitizeDisplayText(body.reason);
  return body;
}

function normalizeChecklistBody(json = {}) {
  const body = { ...(json || {}) };
  if (body.centre || body.center || body.location) body.centre = normalizeCentreValue(body.centre || body.center || body.location);
  if (body.label) body.label = sanitizeDisplayText(body.label);
  if (body.blockNote) body.blockNote = sanitizeDisplayText(body.blockNote);
  if (body.remark) body.remark = sanitizeDisplayText(body.remark);
  if (body.corePracticeAreaTitle) body.corePracticeAreaTitle = sanitizeDisplayText(body.corePracticeAreaTitle);
  if (body.practiceAreaTitle) body.practiceAreaTitle = sanitizeDisplayText(body.practiceAreaTitle);
  if (body.activityKey) body.activityKey = slugifyKey(body.activityKey);
  if (body.sourceFlow) body.sourceFlow = normalizeSourceFlow(body.sourceFlow);
  if (body.competencies !== undefined) body.competencies = sanitizeStringArray(body.competencies);
  if (body.skills !== undefined) body.skills = sanitizeStringArray(body.skills);
  if (body.sourceNotes) body.sourceNotes = sanitizeDisplayText(body.sourceNotes);
  return body;
}

/* ------------------------------- Fetch helper ------------------------------ */
async function fetchJson(url, options = {}) {
  const response = await fetch(url, options);
  const text = await response.text().catch(() => "");
  let data = {};

  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { message: text };
    }
  }

  return { ok: response.ok, status: response.status, data };
}

function makeRequestError(result, path) {
  const msg =
    result?.data?.message ||
    result?.data?.error ||
    result?.data?.detail ||
    `Request failed: ${result?.status || "unknown"}`;

  const err = new Error(msg);
  err.status = result?.status;
  err.data = result?.data;
  err.path = path;
  return err;
}

function dispatchAuthError(result) {
  const status = Number(result?.status || 0);
  const message = String(result?.data?.message || result?.data?.error || "").toLowerCase();
  const isAuthRelated403 =
    status === 403 &&
    /\b(jwt expired|token expired|session expired|invalid token|no token provided|unauthori[sz]ed)\b/.test(message);

  // A generic 403 is an authorization failure, not proof that the CMFM
  // session expired. Do not trigger global sign-out handling for it.
  if (status !== 401 && !isAuthRelated403) return;

  try {
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("cmfm:auth-error", {
          detail: {
            status,
            source: "activityApi",
            message: result?.data?.message || result?.data?.error || "",
          },
        })
      );
    }
  } catch {}
}

function buildHeaders(hasJsonBody = true) {
  const headers = {
    Accept: "application/json",
    "X-Requested-From": "portal-tenant",
    "X-Client": "doctor-frontend",
  };

  if (hasJsonBody) headers["Content-Type"] = "application/json";

  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const slug = getTenantSlug();
  if (slug) {
    headers["X-Tenant-Slug"] = slug;

    // Only send ObjectId in X-Tenant-Id. Never send slug here.
    const tenantId = readTenantIdFromStorage();
    if (tenantId) headers["X-Tenant-Id"] = tenantId;
  }

  return headers;
}

function requestCandidates(path) {
  const plain = path.startsWith("/") ? path : `/${path}`;
  const prefix = tenantPrefix();

  if (!prefix || hasTenantPrefix(plain)) return [plain];

  const prefixed = `${prefix}${plain}`;
  return [prefixed, plain];
}

async function req(path, { method = "GET", json, body, headers: extraHeaders } = {}) {
  const hasJsonBody = json !== undefined;
  const options = {
    method,
    headers: {
      ...buildHeaders(hasJsonBody || !body),
      ...(extraHeaders || {}),
    },
    credentials: "include",
  };

  if (json !== undefined) options.body = JSON.stringify(json);
  else if (body !== undefined) options.body = body;
  else if (method === "GET" || method === "HEAD") {
    delete options.headers["Content-Type"];
  }

  const candidates = requestCandidates(path);
  let last = null;

  for (let i = 0; i < candidates.length; i += 1) {
    const candidate = candidates[i];
    const result = await fetchJson(toUrl(candidate), options);
    if (result.ok) return result.data;

    dispatchAuthError(result);
    last = { result, path: candidate };

    if (!ROUTE_FALLBACK_STATUSES.has(Number(result.status))) break;
  }

  throw makeRequestError(last?.result || {}, last?.path || path);
}

/* ------------------- Route fallback between old & new APIs ------------------ */
// Try multiple candidate routes; only fall back on route-mismatch style statuses.
async function reqTry(paths, opts) {
  const list = Array.isArray(paths) ? paths : [paths];
  let lastErr = null;

  for (const path of list) {
    try {
      return await req(path, opts);
    } catch (err) {
      lastErr = err;
      const status = Number(err?.status || 0);
      const code = String(err?.data?.code || '');
      // Stable backend validation errors are real workflow responses, not route mismatches.
      // Do not probe legacy aliases after them.
      if (status === 400 && /^(ACTIVITY_|TENANT_)/.test(code)) throw err;
      if (!ROUTE_FALLBACK_STATUSES.has(status)) throw err;
    }
  }

  throw lastErr || new Error("Request failed");
}

function decorateSessionParticipant(item) {
  if (!item || typeof item !== "object") return item;
  return {
    ...item,
    name: sanitizeDisplayText(item.name || ""),
    role: sanitizeDisplayText(item.role || ""),
    designation: sanitizeDisplayText(item.designation || ""),
    functions: sanitizeStringArray(item.functions || []),
    plannedParticipationMode: normalizeParticipationMode(item.plannedParticipationMode),
    note: sanitizeDisplayText(item.note || ""),
  };
}

function decorateSessionLike(item) {
  if (!item || typeof item !== "object") return item;
  return {
    ...item,
    templateTitle: sanitizeDisplayText(item.templateTitle || ""),
    corePracticeAreaTitle: sanitizeDisplayText(item.corePracticeAreaTitle || ""),
    centre: normalizeCentreValue(item.centre || ""),
    notes: sanitizeDisplayText(item.notes || ""),
    participants: Array.isArray(item.participants) ? item.participants.map(decorateSessionParticipant) : [],
  };
}

function decorateSessionPayload(data) {
  if (Array.isArray(data)) return data.map(decorateSessionLike);
  if (!data || typeof data !== "object") return data;
  const out = { ...data };
  ["items", "rows"].forEach((key) => {
    if (Array.isArray(out[key])) out[key] = out[key].map(decorateSessionLike);
  });
  if (Array.isArray(out.data)) out.data = out.data.map(decorateSessionLike);
  else if (out.data && typeof out.data === "object") out.data = decorateSessionLike(out.data);
  ["item", "session"].forEach((key) => {
    if (out[key] && typeof out[key] === "object") out[key] = decorateSessionLike(out[key]);
  });
  return out;
}

/* ---------------------------------- API ---------------------------------- */
export const activityApi = {
  /* Templates */
  listTemplates: async (q = {}) => {
    const qs = queryString(q);
    const data = await reqTry(
      [
        `/api/activity-templates${qs}`,
        `/api/activity-templates/list${qs}`,
        `/api/activity-templates/catalog${qs}`,
        `/api/activity-templates/catalogue${qs}`,
        `/api/activityTemplates${qs}`,
        `/api/intern-guided-flows/templates${qs}`,
      ],
      withMethod("GET")
    );

    // ActivityTemplate is authoritative. Preserve a genuine empty server
    // catalogue and surface request failures instead of synthesising templates.
    return rememberTemplatePayload(data);
  },

  listActiveTemplates: async (q = {}) => {
    const cacheable =
      Object.keys(cleanQueryParams(q)).every((key) => ["active", "limit", "page"].includes(key)) &&
      cleanBoolean(q.active, true) !== false;

    if (cacheable && isTemplateCacheFresh()) {
      return templateCache.payload;
    }

    if (cacheable && templateListInFlight) {
      return templateListInFlight;
    }

    const load = async () => {
      const qs = queryString({ ...q, active: q.active ?? true });
      const data = await reqTry(
        [
          `/api/activity-templates/active${qs}`,
          `/api/activity-templates${qs}`,
          `/api/activityTemplates${qs}`,
        ],
        withMethod("GET")
      );
      return cacheable
        ? rememberTemplatePayload(data)
        : decorateTemplatePayload(data);
    };

    if (!cacheable) return load();

    templateListInFlight = load().finally(() => {
      templateListInFlight = null;
    });

    return templateListInFlight;
  },

  getTemplate: async (idOrKey) => {
    const cached = findCachedTemplate(idOrKey);
    if (cached) return asTemplateItemResponse(cached, { fromCache: true });

    // Resolve quick-action keys from one shared active-template catalogue before
    // issuing per-key requests. A failed catalogue request does not suppress the
    // existing detail-route fallbacks below.
    try {
      const catalogue = await activityApi.listActiveTemplates({ active: true, limit: 500 });
      const wantedRaw = String(idOrKey || "").trim().toLowerCase();
      const wantedSlug = slugifyKey(idOrKey);
      const match = pickArrayPayload(catalogue).find((item) => {
        const identities = templateIdentityValues(item);
        return identities.includes(wantedRaw) || (wantedSlug && identities.includes(wantedSlug));
      });
      if (match) return asTemplateItemResponse(match, { fromCatalog: true });
    } catch {}

    const id = encodePathValue(idOrKey);
    const data = await reqTry(
      [
        `/api/activity-templates/${id}`,
        `/api/activity-templates/slug/${id}`,
        `/api/activity-templates/key/${id}`,
        `/api/activityTemplates/${id}`,
      ],
      withMethod("GET")
    );
    return decorateTemplatePayload(data);
  },

  createTemplate: async (json = {}) => {
    const data = await reqTry(
      [
        "/api/activity-templates",
        "/api/activity-templates/create",
        "/api/activityTemplates",
      ],
      { method: "POST", json: normalizeTemplateBody(json) }
    );
    clearTemplateCache();
    return decorateTemplatePayload(data);
  },

  upsertTemplate: async (json = {}) => {
    const data = await reqTry(
      [
        "/api/activity-templates/upsert",
        "/api/activity-templates",
        "/api/activityTemplates/upsert",
      ],
      { method: "POST", json: normalizeTemplateBody(json) }
    );
    clearTemplateCache();
    return decorateTemplatePayload(data);
  },

  updateTemplate: async (id, json = {}) => {
    const safeId = encodePathValue(id);
    const data = await reqTry(
      [
        `/api/activity-templates/${safeId}`,
        `/api/activity-templates/${safeId}/update`,
        `/api/activityTemplates/${safeId}`,
      ],
      { method: "PATCH", json: normalizeTemplateBody(json) }
    );
    clearTemplateCache();
    return decorateTemplatePayload(data);
  },

  setTemplateActive: async (id, active = true) => {
    const safeId = encodePathValue(id);
    const data = await reqTry(
      [
        `/api/activity-templates/${safeId}/active`,
        `/api/activity-templates/${safeId}/status`,
        `/api/activity-templates/${safeId}`,
      ],
      { method: "PATCH", json: { active: cleanBoolean(active, true) } }
    );
    clearTemplateCache();
    return decorateTemplatePayload(data);
  },

  deleteTemplate: async (id, opts = {}) => {
    const safeId = encodePathValue(id);
    const qs = queryString({ hard: opts.hard ? 1 : undefined });
    const data = await reqTry(
      [
        `/api/activity-templates/${safeId}${qs}`,
        `/api/activityTemplates/${safeId}${qs}`,
      ],
      withMethod("DELETE")
    );
    clearTemplateCache();
    return decorateTemplatePayload(data);
  },

  /* Guided activities: intern start/save/submit */
  startActivity: async (json = {}) => {
    const payload = normalizeActivityBody(json);

    const data = await reqTry(
      [
        "/api/activities/start",
        "/api/activities/begin",
        "/api/activities/launch",
        "/api/activity-instances/start",
        "/api/activityInstances/start",
        "/api/intern/activity-instances/start",
      ],
      { method: "POST", json: payload }
    );
    return decorateActivityPayload(data);
  },

  saveActivity: async (id, json = {}) => {
    const safeId = encodePathValue(id);
    const payload = normalizeActivityBody(json);

    const data = await reqTry(
      [
        `/api/activities/${safeId}/save`,
        `/api/activities/${safeId}/draft`,
        `/api/activity-instances/${safeId}/save`,
        `/api/activity-instances/${safeId}/draft`,
        `/api/activityInstances/${safeId}/save`,
        `/api/intern/activity-instances/${safeId}/save`,
      ],
      { method: "PUT", json: payload }
    );
    return decorateActivityPayload(data);
  },

  saveActivityDraft: async (id, json = {}) => {
    const safeId = encodePathValue(id);
    const payload = normalizeActivityBody(json);

    const data = await reqTry(
      [
        `/api/activities/${safeId}/draft`,
        `/api/activities/${safeId}/save`,
        `/api/activity-instances/${safeId}/draft`,
        `/api/activity-instances/${safeId}/save`,
      ],
      { method: "POST", json: payload }
    );
    return decorateActivityPayload(data);
  },

  submitForObservation: async (id, json = {}) => {
    const safeId = encodePathValue(id);
    const payload = Object.keys(json || {}).length ? normalizeActivityBody(json) : undefined;

    const data = await reqTry(
      [
        `/api/activities/${safeId}/submit-observation`,
        `/api/activities/${safeId}/submit`,
        `/api/activities/${safeId}/submit-to-observer`,
        `/api/activities/${safeId}/submit-to-approver`,
        `/api/activities/${safeId}/send-for-approval`,
        `/api/activity-instances/${safeId}/submit-observation`,
        `/api/activity-instances/${safeId}/submit`,
        `/api/activityInstances/${safeId}/submit-observation`,
        `/api/intern/activity-instances/${safeId}/submit-observation`,
      ],
      payload ? { method: "POST", json: payload } : withMethod("POST")
    );
    return decorateActivityPayload(data);
  },

  /* Observer / reviewer */
  observerSignoff: async (id, json = {}) => {
    const safeId = encodePathValue(id);
    const payload = normalizeDecisionBody(json);

    const data = await reqTry(
      [
        `/api/activities/${safeId}/observer-signoff`,
        `/api/activities/${safeId}/record-decision`,
        `/api/activities/${safeId}/recordDecision`,
        `/api/activities/${safeId}/decision`,
        `/api/activities/${safeId}/approve`,
        `/api/activities/${safeId}/return`,
        `/api/activity-instances/${safeId}/observer-signoff`,
        `/api/activity-instances/${safeId}/record-decision`,
        `/api/activityInstances/${safeId}/observer-signoff`,
        `/api/intern/activity-instances/${safeId}/observer-signoff`,
      ],
      { method: "POST", json: payload }
    );
    return decorateActivityPayload(data);
  },

  returnActivity: async (id, json = {}) => {
    return activityApi.observerSignoff(id, { ...json, decision: "returned" });
  },

  approveActivity: async (id, json = {}) => {
    return activityApi.observerSignoff(id, { ...json, decision: "approved" });
  },

  repeatActivity: async (id, json = {}) => {
    return activityApi.observerSignoff(id, { ...json, decision: "repeat", repeat: true });
  },

  /* Lists & details */
  listAll: async (q = {}) => {
    const qs = queryString(q);
    const data = await reqTry(
      [
        `/api/activities${qs}`,
        `/api/activities/list${qs}`,
        `/api/activities/all${qs}`,
        `/api/activity-instances${qs}`,
        `/api/activityInstances${qs}`,
        `/api/intern/activity-instances${qs}`,
      ],
      withMethod("GET")
    );
    return decorateActivityPayload(data);
  },

  listMine: async (q = {}) => {
    const qs = queryString(q);
    const data = await reqTry(
      [
        `/api/activities/me${qs}`,
        `/api/activities/mine${qs}`,
        `/api/activities/self${qs}`,
        `/api/activity-instances/me${qs}`,
        `/api/activity-instances/mine${qs}`,
        `/api/activityInstances/me${qs}`,
        `/api/intern/activity-instances/me${qs}`,
      ],
      withMethod("GET")
    );
    return decorateActivityPayload(data);
  },

  reviewerQueue: async (q = {}) => {
    const qs = queryString(q);
    const data = await reqTry(
      [
        `/api/activities/reviewer-queue${qs}`,
        `/api/activities/reviewerQueue${qs}`,
        `/api/activities/reviewerqueue${qs}`,
        `/api/activities/observer-queue${qs}`,
        `/api/activities/queue${qs}`,
        `/api/activity-instances/reviewer-queue${qs}`,
        `/api/activity-instances/observer-queue${qs}`,
        `/api/activityInstances/reviewer-queue${qs}`,
        `/api/intern/activity-instances/reviewer-queue${qs}`,
      ],
      withMethod("GET")
    );
    return decorateActivityPayload(data);
  },

  observerQueue: async (q = {}) => {
    return activityApi.reviewerQueue(q);
  },

  listSubmitted: async (q = {}) => {
    const qs = queryString(q);
    const data = await reqTry(
      [
        `/api/activities/submitted${qs}`,
        `/api/activities/pending${qs}`,
        `/api/activities/reviewer-queue${qs}`,
      ],
      withMethod("GET")
    );
    return decorateActivityPayload(data);
  },

  listReturned: async (q = {}) => {
    const qs = queryString(q);
    const data = await reqTry(
      [
        `/api/activities/returned${qs}`,
        `/api/activities/me${queryString({ ...q, status: q.status || "returned,observed-returned,returned-to-edit,repeat,observed-repeat" })}`,
      ],
      withMethod("GET")
    );
    return decorateActivityPayload(data);
  },

  listApproved: async (q = {}) => {
    const qs = queryString(q);
    const data = await reqTry(
      [
        `/api/activities/approved${qs}`,
        `/api/activities${queryString({ ...q, status: q.status || "approved,observed-approved" })}`,
      ],
      withMethod("GET")
    );
    return decorateActivityPayload(data);
  },

  getActivity: async (id) => {
    const safeId = encodePathValue(id);
    const data = await reqTry(
      [
        `/api/activities/${safeId}`,
        `/api/activity-instances/${safeId}`,
        `/api/activityInstances/${safeId}`,
        `/api/intern/activity-instances/${safeId}`,
      ],
      withMethod("GET")
    );
    return decorateActivityPayload(data);
  },

  /* Activity teams / sessions */
  teamCandidates: async (q = {}) => {
    const qs = queryString(q);
    return reqTry(
      [
        `/api/activities/team-candidates${qs}`,
        `/api/activities/teamCandidates${qs}`,
      ],
      withMethod("GET")
    );
  },

  createSession: async (json = {}) => {
    const payload = normalizeActivityBody(json);
    const data = await req('/api/activities/sessions', { method: "POST", json: payload });
    return decorateSessionPayload(data);
  },

  listSessions: async (q = {}) => {
    const data = await req(`/api/activities/sessions${queryString(q)}`, withMethod("GET"));
    return decorateSessionPayload(data);
  },

  getSession: async (id) => {
    const safeId = encodePathValue(id);
    const data = await req(`/api/activities/sessions/${safeId}`, withMethod("GET"));
    return decorateSessionPayload(data);
  },

  updateSession: async (id, json = {}) => {
    const safeId = encodePathValue(id);
    const data = await req(`/api/activities/sessions/${safeId}`, { method: "PATCH", json: normalizeActivityBody(json) });
    return decorateSessionPayload(data);
  },

  amendParticipation: async (id, json = {}) => {
    const safeId = encodePathValue(id);
    const payload = normalizeDecisionBody(json);
    const data = await req(`/api/activities/${safeId}/amend-participation`, { method: "PATCH", json: payload });
    return decorateActivityPayload(data);
  },

  /* Intern Rotation Checklist */
  getRotationChecklistCurrent: async (q = {}) => {
    const qs = queryString(q);
    const data = await reqTry(
      [
        `/api/intern-rotation-checklists/my/current${qs}`,
        `/api/intern-rotation-checklists/current${qs}`,
        `/api/intern-rotation-checklists/my${qs}`,
        `/api/intern-rotation-checklists/mine/current${qs}`,
        `/api/internRotationChecklists/my/current${qs}`,
        `/api/internRotationChecklists/current${qs}`,
        `/api/intern/rotation-checklists/my/current${qs}`,
        `/api/intern/rotation-checklists/current${qs}`,
      ],
      withMethod("GET")
    );
    return decorateChecklistPayload(data);
  },

  upsertRotationChecklistBlock: async (json = {}) => {
    const payload = normalizeChecklistBody(json);
    const data = await reqTry(
      [
        "/api/intern-rotation-checklists/upsert",
        "/api/intern-rotation-checklists/block/upsert",
        "/api/internRotationChecklists/upsert",
        "/api/intern/rotation-checklists/upsert",
      ],
      { method: "POST", json: payload }
    );
    return decorateChecklistPayload(data);
  },

  incrementRotationChecklistCell: async (json = {}) => {
    const payload = normalizeChecklistBody(json);
    const data = await reqTry(
      [
        "/api/intern-rotation-checklists/increment",
        "/api/intern-rotation-checklists/cell/increment",
        "/api/intern-rotation-checklists/adjust",
        "/api/internRotationChecklists/increment",
        "/api/intern/rotation-checklists/increment",
      ],
      { method: "POST", json: payload }
    );
    return decorateChecklistPayload(data);
  },

  /* Utility methods for screens that need raw but still tenant-safe requests */
  request: req,
  requestTry: reqTry,
  pickArrayPayload,
  pickItemPayload,
  clearTemplateCache,
};

export default activityApi;
